/* ============================================================
   SCREEN — the glass tube and everything drawn inside it.
   Pure projection of the store: screen mode, bumper, OSDs, gate,
   no-signal caption, EPG and CRT glass all read from useTv().
   ============================================================ */
import { Suspense } from 'react'
import { useTv } from '@/tv/context'
import { Bumper } from './Bumper'
import { Osd } from './Osd'
import { Epg } from './Epg'
import { channelComponent } from './channelComponents'
import styles from './Screen.module.css'

export function Screen() {
  const tv = useTv()
  const { power, screen, audio, screenInteractive, activeComponent } = tv.state
  // a component QUEUE ITEM (e.g. HIDEO) renders a custom interactive component AS the content; the STAGE's
  // component presenter owns `activeComponent` (sets/clears it as part of its content transitions). Legacy
  // channel-level `kind:'component'` channels still resolve here too (back-compat) until they migrate.
  // NEVER while the set is OFF — a component (e.g. HIDEO) that powers the TV off must vanish with it, not
  // linger on a dead screen.
  const componentKey = power === 'on' ? (activeComponent ?? (tv.current.kind === 'component' ? tv.current.component : null)) : null
  const ChannelComp = componentKey ? channelComponent(componentKey) : null

  // Mirror the prototype's #screen class set, derived from store state.
  const cls = [
    styles.screen,
    screen === 'off' && styles.isOff,
    screen === 'snow' && styles.isSnow,
    screen === 'tuning' && styles.isTuning,
    screen === 'playing' && styles.isPlaying,
    audio === 'awaiting-gesture' && styles.isLocked,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div className={cls} ref={tv.registerScreen} data-screen-label="CRT Screen" tabIndex={-1} onClick={tv.whackScreen}>
      <div className={styles.content}>
        {/* Two stacked YouTube frames: A active, B pre-buffers the next video so
            transitions are instant (DualPlayer swaps opacity between them). */}
        <div className={styles.playerFrame} ref={tv.registerPlayerHostA} />
        <div className={styles.playerFrame} ref={tv.registerPlayerHostB} />

        {/* Click-catch over the picture: a click here is the remote play/pause button
            (act('play')). Enabled ONLY while the picture is ours-to-control (playing or we paused);
            inert while locked or when YouTube has the wheel (bot-check / sign-in / stall) so the
            click falls through to the iframe and the viewer can dismiss YouTube's overlay. */}
        <div
          className={[styles.clickCatch, audio === 'unlocked' && screenInteractive && styles.clickCatchOn].filter(Boolean).join(' ')}
          onClick={(e) => { e.stopPropagation(); tv.act('play') }}
          aria-hidden="true"
        />

        {/* analog snow */}
        <canvas className={styles.snow} ref={tv.registerSnow} />

        {/* no-signal caption — never under an interactive component channel (e.g. HIDEO) */}
        {ChannelComp ? null : (
          <div className={styles.noSignal}>
            <div className={styles.nsBox} ref={tv.registerNoSignalBox}>
              NO SIGNAL<small>PLEASE STAND BY</small>
            </div>
          </div>
        )}

        {/* START GATE prompt — click-through so the click lands on the iframe */}
        <div className={styles.soundBadge} aria-hidden="true">
          <div className={styles.faultNote}>
            <span className={styles.fist}>
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M5 11V8a2 2 0 0 1 4 0M9 11V6.5a2 2 0 0 1 4 0V11M13 11V8a2 2 0 0 1 4 0v5a6 6 0 0 1-6 6h-1.5a5 5 0 0 1-3.9-1.9L4 14.5a1.8 1.8 0 0 1 2.8-2.2L8 14" />
              </svg>
            </span>
            <span className={styles.faultText}>
              MUTED<small>WHACK ME TO START</small>
            </span>
          </div>
        </div>

        {/* blocked hint: YouTube refused playback (IP bot-flag). Non-covering + click-through so
            the viewer can still reach YouTube's error/sign-in UI on the frame underneath. */}
        {tv.state.blocked ? (
          <div className={styles.blockedHint} aria-hidden="true">
            <span>SIGNAL BLOCKED</span>
            <small>YouTube wants you to sign in — click the picture, or open it on youtube.com</small>
          </div>
        ) : null}

        {/* interactive component channel (e.g. HIDEO) — covers the picture/snow when tuned.
            ChannelComp is a STABLE module-level lazy() from the registry, not created during render. */}
        {/* eslint-disable-next-line react-hooks/static-components */}
        {ChannelComp ? <Suspense fallback={null}><ChannelComp /></Suspense> : null}

        {/* channel ident bumper */}
        <Bumper />

        {/* OSDs: channel badge, now-playing banner, volume */}
        <Osd />

        {/* EPG / TV guide */}
        <Epg />

        {/* power flashes */}
        <div className={styles.warmupFx} />
        <div className={styles.collapseFx} />

        {/* CRT brightness flicker — opacity-only veil (never a filter on the video) */}
        <div className={styles.flicker} />

        {/* CRT glass */}
        <div className={styles.crt}>
          <div className={styles.crtBulge} />
          <div className={styles.crtRoll} />
          <div className={styles.crtScanlines} />
          <div className={styles.crtFringe} />
          <div className={styles.crtVignette} />
          <div className={styles.crtGlare} />
        </div>
      </div>
    </div>
  )
}
